import { useEffect, useState } from "react";
import { Plus, Handshake, Percent, BadgeDollarSign, Eye, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";

function formatRp(n) { return `Rp ${Number(n || 0).toLocaleString("id-ID")}`; }

const EMPTY_CREATE = { name: "", email: "", phone: "", password: "", commission_fixed: 0, commission_percentage: 0 };

export default function AdminAffiliate() {
  const [items, setItems] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CREATE);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const load = () => axios.get(`${API}/admin/affiliate`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!detailId) { setDetail(null); return; }
    axios.get(`${API}/admin/affiliate/${detailId}`).then(({ data }) => { setDetail(data); setEditForm({ commission_fixed: data.commission_fixed, commission_percentage: data.commission_percentage, is_active: data.is_active }); }).catch(() => setDetail(null));
  }, [detailId]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.phone || !form.password) return toast.error("Mohon lengkapi semua data agen");
    try {
      await axios.post(`${API}/admin/affiliate`, form);
      toast.success("Akun agen berhasil dibuat");
      setModalOpen(false);
      setForm(EMPTY_CREATE);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const handleUpdateAffiliate = async () => {
    try {
      await axios.put(`${API}/admin/affiliate/${detailId}`, editForm);
      toast.success("Data agen berhasil diperbarui");
      const { data } = await axios.get(`${API}/admin/affiliate/${detailId}`);
      setDetail(data);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const markPaid = async (commissionId) => {
    try {
      await axios.put(`${API}/admin/affiliate/komisi/${commissionId}/paid`);
      toast.success("Komisi ditandai sudah dibayar");
      const { data } = await axios.get(`${API}/admin/affiliate/${detailId}`);
      setDetail(data);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-affiliate-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Affiliate / Agen</h1>
          <p className="text-sm text-[#64748B]">Kelola akun agen mitra pendaftaran dan komisi fee/bonus mereka.</p>
        </div>
        <Button data-testid="admin-affiliate-add-button" onClick={() => setModalOpen(true)} className="gap-2 rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#F5B700] text-white hover:opacity-90"><Plus className="h-4 w-4" /> Buat Akun Agen</Button>
      </div>

      <div className="flex justify-end">
        <Button
          variant="outline"
          data-testid="admin-affiliate-export-button"
          onClick={() => window.open(`${API}/admin/export/affiliate`, "_blank")}
          className="gap-2 rounded-full"
        >
          <Download className="h-4 w-4" /> Unduh CSV Agen &amp; Komisi
        </Button>
      </div>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={Handshake} title="Belum ada agen" description="Buat akun agen pertama untuk mulai program affiliate." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-affiliate-table">
            <TableHeader><TableRow><TableHead>Nama Agen</TableHead><TableHead>Kode Referral</TableHead><TableHead>Jamaah</TableHead><TableHead>Komisi Pending</TableHead><TableHead>Komisi Dibayar</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} data-testid={`admin-affiliate-row-${a.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{a.name}</TableCell>
                  <TableCell className="font-mono text-[#0F6B4F]">{a.code}</TableCell>
                  <TableCell className="text-[#64748B]">{a.total_referred}</TableCell>
                  <TableCell className="text-amber-600">{formatRp(a.pending_commission)}</TableCell>
                  <TableCell className="text-emerald-600">{formatRp(a.paid_commission)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${a.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{a.is_active ? "Aktif" : "Nonaktif"}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`admin-affiliate-detail-${a.id}`} onClick={() => setDetailId(a.id)} className="gap-1 rounded-full"><Eye className="h-3.5 w-3.5" /> Detail</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="admin-affiliate-create-modal">
          <DialogHeader><DialogTitle>Buat Akun Agen Baru</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Agen</Label><Input data-testid="affiliate-form-name" value={form.name} onChange={(e) => update("name", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input data-testid="affiliate-form-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} /></div>
              <div><Label>Nomor HP</Label><Input data-testid="affiliate-form-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} /></div>
            </div>
            <div><Label>Password</Label><Input data-testid="affiliate-form-password" type="password" value={form.password} onChange={(e) => update("password", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fee Tetap per Jamaah (Rp)</Label><Input data-testid="affiliate-form-commission-fixed" type="number" value={form.commission_fixed} onChange={(e) => update("commission_fixed", Number(e.target.value))} /></div>
              <div><Label>Persentase Komisi (%)</Label><Input data-testid="affiliate-form-commission-percentage" type="number" value={form.commission_percentage} onChange={(e) => update("commission_percentage", Number(e.target.value))} /></div>
            </div>
            <Button data-testid="admin-affiliate-create-submit" onClick={handleCreate} className="w-full rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#F5B700] text-white hover:opacity-90">Buat Akun Agen</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)}>
        <DialogContent data-testid="admin-affiliate-detail-modal" className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.name || "Detail Agen"}</DialogTitle></DialogHeader>
          {!detail ? <LoadingScreen /> : (
            <div className="space-y-5">
              <div className="rounded-xl bg-[#F5F9F7] p-4 text-sm">
                <p>Kode Referral: <span className="font-mono font-bold text-[#0F6B4F]">{detail.code}</span></p>
                <p className="mt-1 text-[#64748B]">Link: {`${window.location.origin}/register?ref=${detail.code}`}</p>
              </div>

              {editForm && (
                <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-100 p-4">
                  <div><Label className="flex items-center gap-1"><BadgeDollarSign className="h-3.5 w-3.5" /> Fee Tetap (Rp)</Label><Input data-testid="affiliate-detail-commission-fixed" type="number" value={editForm.commission_fixed} onChange={(e) => setEditForm((f) => ({ ...f, commission_fixed: Number(e.target.value) }))} /></div>
                  <div><Label className="flex items-center gap-1"><Percent className="h-3.5 w-3.5" /> Persentase (%)</Label><Input data-testid="affiliate-detail-commission-percentage" type="number" value={editForm.commission_percentage} onChange={(e) => setEditForm((f) => ({ ...f, commission_percentage: Number(e.target.value) }))} /></div>
                  <div className="col-span-2 flex items-center justify-between">
                    <Label>Status Aktif</Label>
                    <Switch checked={editForm.is_active} onCheckedChange={(v) => setEditForm((f) => ({ ...f, is_active: v }))} data-testid="affiliate-detail-active-switch" />
                  </div>
                  <Button data-testid="affiliate-detail-save-button" onClick={handleUpdateAffiliate} className="col-span-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Simpan Perubahan</Button>
                </div>
              )}

              <div>
                <h4 className="font-heading text-sm font-semibold text-[#1F2937]">Jamaah Direferensikan ({detail.referred_jamaah?.length || 0})</h4>
                <div className="mt-2 space-y-2">
                  {(detail.referred_jamaah || []).length === 0 ? (
                    <p className="text-sm text-[#64748B]">Belum ada jamaah yang mendaftar melalui agen ini.</p>
                  ) : detail.referred_jamaah.map((j) => (
                    <div key={j.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 text-sm">
                      <span className="font-medium text-[#1F2937]">{j.name}</span>
                      <StatusBadge status={j.status} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-heading text-sm font-semibold text-[#1F2937]">Riwayat Komisi</h4>
                <div className="mt-2 space-y-2">
                  {(detail.commissions || []).length === 0 ? (
                    <p className="text-sm text-[#64748B]">Belum ada komisi tercatat.</p>
                  ) : detail.commissions.map((c) => (
                    <div key={c.id} data-testid={`affiliate-commission-row-${c.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 text-sm">
                      <div>
                        <p className="font-medium text-[#1F2937]">{formatRp(c.total_commission)}</p>
                        <p className="text-xs text-[#64748B]">{c.program_name || "-"}</p>
                      </div>
                      {c.status === "pending" ? (
                        <Button size="sm" data-testid={`affiliate-commission-mark-paid-${c.id}`} onClick={() => markPaid(c.id)} className="rounded-full bg-[#F5B700] text-[#1F2937] hover:opacity-90">Tandai Lunas</Button>
                      ) : (
                        <StatusBadge status="paid" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
